import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateOrUpdateAbsentReportMutation } from '../../../../hooks/mutations/useReportMutation';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { formatName } from '../../../../utils/capitalize';
import DropDown from '../../../DropDown';
import ModalBody from '../../ModalBody';
import { DataItem, Report } from '../../../../services/apiSLTP/activities';
import { permissionLevels, reportTypeEnum } from '../../../../utils/enums';
import { isSameDayOrAfter } from '../../../../utils/dates';

interface ModalProps {
    open: boolean;
    handleClose: () => void;
    refetch?: () => void;
    initUserId?: string;
    currentDate: Date;
    reports: Report[];
    activities: DataItem[];
}

const CreateAbsentReportModal: React.FC<ModalProps> = ({
    open,
    handleClose,
    refetch = () => {},
    initUserId = '',
    currentDate,
    reports,
    activities,
}) => {
    const [selectedUser, setSelectedUser] = useState({ label: '', value: '' });

    const { t } = useTranslation();

    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });

    const { mutate: createReport, isLoading: isCreateReportLoading } = useCreateOrUpdateAbsentReportMutation({
        onSuccess: (data) => {
            refetch();
            handleClose();
        },
    });

    useEffect(() => {
        if (initUserId.length) {
            const tmpUser = dataUsers?.find((user) => user._id === initUserId && !user.isSpecial);
            setSelectedUser({
                label: tmpUser ? formatName(tmpUser.firstName, tmpUser.lastName) : '',
                value: tmpUser?._id || '',
            });
        }
    }, []);

    return (
        <ModalBody
            closeText={t('cancel')}
            okText={t('validate')}
            title={t('absence')}
            subTitle={t('add')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                createReport({ body: { userId: selectedUser.value, date: currentDate.toISOString() } });
            }}
            disabledOk={!selectedUser.value.length}
        >
            <DropDown
                selectedItem={selectedUser}
                items={
                    dataUsers
                        ?.filter(
                            (user) =>
                                !user.isSpecial &&
                                reports.find((report) => report.userId._id === user._id)?.reportType !== reportTypeEnum.ABSENT &&
                                user.profileId.permsLevel >= permissionLevels.TERRAIN &&
                                !activities.map((activity) => activity.reportId.userId._id).includes(user._id) &&
                                (!user.arrivalDate || (user.arrivalDate && isSameDayOrAfter(currentDate, new Date(user.arrivalDate))))
                        )
                        ?.map((user) => ({
                            label: formatName(user.firstName, user.lastName),
                            value: user._id,
                        })) || []
                }
                handleChange={(event, value) => setSelectedUser({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('person')}
                loading={isUsersLoading}
                required
            />
        </ModalBody>
    );
};

export default CreateAbsentReportModal;
